import {
  buildUpdatePayload,
  canEditSingleDeliveryWindowFields,
  createEditableOrderDraft,
} from "../hooks/useDataManagerOrdersUtils.js";

function resolveSegmentEditableOrder(segment = null) {
  const editableOrder = segment?.businessStep?.orderDetails?.editableOrder;
  return editableOrder && typeof editableOrder === "object" ? editableOrder : null;
}

function resolveSegmentStepType(segment = null) {
  return typeof segment?.businessStep?.stepType === "string"
    ? segment.businessStep.stepType.trim()
    : "";
}

function isStrictP2P3Priority(priority, enforceP2P3TimeWindowsAsHard) {
  if (!enforceP2P3TimeWindowsAsHard || typeof priority !== "string") {
    return false;
  }

  return ["P2", "P3"].includes(priority.trim().toUpperCase());
}

function resolveDraftResetState(editableOrder, stepType, { enforceP2P3TimeWindowsAsHard = false } = {}) {
  const nextDraft = createEditableOrderDraft(editableOrder);

  if (stepType === "loading") {
    if (isStrictP2P3Priority(nextDraft.loading_priority, enforceP2P3TimeWindowsAsHard)) {
      return null;
    }

    const clearsTime = Boolean(nextDraft.loading_window_start);
    const clearsPriority = Boolean(nextDraft.loading_priority);
    if (!clearsTime && !clearsPriority) {
      return null;
    }

    nextDraft.loading_window_start = "";
    nextDraft.loading_priority = "";
    return { clearsPriority, clearsTime, draft: nextDraft };
  }

  if (stepType === "delivery") {
    if (!canEditSingleDeliveryWindowFields(editableOrder)) {
      return null;
    }

    if (isStrictP2P3Priority(nextDraft.delivery_priority, enforceP2P3TimeWindowsAsHard)) {
      return null;
    }

    const clearsTime = Boolean(nextDraft.delivery_window_start);
    const clearsPriority = Boolean(nextDraft.delivery_priority);
    if (!clearsTime && !clearsPriority) {
      return null;
    }

    nextDraft.delivery_window_start = "";
    nextDraft.delivery_priority = "";
    return { clearsPriority, clearsTime, draft: nextDraft };
  }

  return null;
}

export function buildSegmentTimePriorityResetUpdate(
  segment = null,
  { enforceP2P3TimeWindowsAsHard = false } = {},
) {
  const editableOrder = resolveSegmentEditableOrder(segment);
  const orderId = typeof editableOrder?.order_id === "string" ? editableOrder.order_id.trim() : "";
  const stepType = resolveSegmentStepType(segment);
  if (!editableOrder || !orderId || !stepType) {
    return null;
  }

  const resetState = resolveDraftResetState(editableOrder, stepType, {
    enforceP2P3TimeWindowsAsHard,
  });
  if (!resetState) {
    return null;
  }

  return {
    clearsPriority: resetState.clearsPriority,
    clearsTime: resetState.clearsTime,
    orderId,
    payload: buildUpdatePayload(editableOrder, resetState.draft),
    stepType,
  };
}

export function buildSegmentTimePriorityResetUpdates(
  segments = [],
  { enforceP2P3TimeWindowsAsHard = false, stepType = "" } = {},
) {
  const normalizedStepType = typeof stepType === "string" ? stepType.trim() : "";
  const updatesByKey = new Map();

  (Array.isArray(segments) ? segments : []).forEach((segment) => {
    if (normalizedStepType && resolveSegmentStepType(segment) !== normalizedStepType) {
      return;
    }

    const update = buildSegmentTimePriorityResetUpdate(segment, {
      enforceP2P3TimeWindowsAsHard,
    });
    if (!update) {
      return;
    }

    updatesByKey.set(`${update.orderId}:${update.stepType}`, update);
  });

  return Array.from(updatesByKey.values());
}
